/**
 * PatientPortalCard — admin widget for the wellness client profile.
 * Generates / shows / rotates the patient-portal magic link.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Sparkles, Copy, MessageSquare, RefreshCw, Loader2, ShieldCheck, ExternalLink,
  Bell, Send,
} from "lucide-react";

const PatientPortalCard = ({ clientId, clientName, t }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showPush,  setShowPush]  = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/patient-portal/issue/${clientId}`, {}, { withCredentials: true });
      setInfo(r.data);
    } catch {
      // silent — likely 404 on first run if client lookup failed
    } finally {
      setBusy(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    if (!window.confirm(t("Rigenerare il link? Il vecchio non sarà più valido.", "Regenerate the link? The previous one will be invalidated."))) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/patient-portal/regenerate/${clientId}`, {}, { withCredentials: true });
      setInfo(r.data);
      toast.success(t("Link rigenerato", "Link regenerated"));
    } catch {
      toast.error(t("Errore", "Error"));
    } finally {
      setBusy(false);
    }
  };

  if (!info) {
    return (
      <div className="text-center text-xs text-slate-400 py-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("Nessun portale ancora", "No portal yet")}
      </div>
    );
  }

  const fullUrl = `${window.location.origin}${info.public_url}`;

  return (
    <div className="space-y-2" data-testid="patient-portal-card">
      <div className="rounded-2xl bg-gradient-to-br from-teal-500/10 to-emerald-500/10 border border-teal-200 dark:border-teal-900/40 p-2.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-teal-700 dark:text-teal-300">
          <Sparkles className="w-3 h-3" /> {t("Link attivo", "Active link")}
        </div>
        <p className="text-[10px] font-mono break-all text-slate-600 dark:text-slate-300 mt-1 leading-tight">
          {fullUrl}
        </p>
        {info.claimed && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
            <ShieldCheck className="w-2.5 h-2.5" /> {t("Account email attivato", "Email account active")}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <Button
          size="sm"
          onClick={() => setShowShare(true)}
          className="h-7 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-[11px]"
          data-testid="portal-share-btn"
        >
          <MessageSquare className="w-3 h-3 mr-1" /> {t("Condividi", "Share")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={regenerate}
          disabled={busy}
          className="h-7 rounded-full text-[11px]"
          data-testid="portal-regenerate-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          {t("Rigenera", "Rotate")}
        </Button>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setShowPush(true)}
        className="w-full h-7 rounded-full text-[11px] text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-900/30"
        data-testid="portal-push-btn"
      >
        <Bell className="w-3 h-3 mr-1" /> {t("Invia notifica", "Send notification")}
      </Button>
      <ShareDialog
        open={showShare}
        onOpenChange={setShowShare}
        url={fullUrl}
        clientName={clientName}
        t={t}
      />
      <SendPushDialog
        open={showPush}
        onOpenChange={setShowPush}
        clientId={clientId}
        clientName={clientName}
        t={t}
      />
    </div>
  );
};

const SendPushDialog = ({ open, onOpenChange, clientId, clientName, t }) => {
  const [title, setTitle] = useState("");
  const [body,  setBody]  = useState("");
  const [busy,  setBusy]  = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(t("Promemoria dallo studio", "Reminder from your studio"));
      setBody("");
    }
  }, [open, t]);

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error(t("Compila titolo e messaggio", "Fill title and body"));
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/patient-portal/push/send/${clientId}`, {
        title: title.trim(),
        body:  body.trim(),
      }, { withCredentials: true });
      const n = r.data?.sent ?? 0;
      if (n === 0) {
        toast.warning(t("Il paziente non ha ancora attivato le notifiche", "The patient hasn't enabled notifications yet"));
      } else {
        toast.success(t(`Inviata a ${n} dispositiv${n===1?"o":"i"}`, `Sent to ${n} device${n===1?"":"s"}`));
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Errore invio", "Send error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="portal-push-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Bell className="w-3.5 h-3.5 text-white" />
            </div>
            {t("Invia notifica push", "Send push notification")}
          </DialogTitle>
          <DialogDescription>
            {t(`Manda un breve messaggio a ${clientName || "questo paziente"}. Apparirà come notifica sul suo dispositivo.`,
               `Send a brief message to ${clientName || "this patient"}. It will pop up as a device notification.`)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Titolo", "Title")}</label>
            <Input
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={80}
              data-testid="portal-push-title"
              className="rounded-2xl"
            />
            <p className="text-[10px] text-slate-400 text-right">{title.length}/80</p>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("Messaggio", "Message")}</label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              maxLength={240}
              rows={3}
              placeholder={t("Es. Ricordati la routine di stretching prima di dormire", "E.g. Don't forget your stretching routine before bed")}
              data-testid="portal-push-body"
              className="rounded-2xl"
            />
            <p className="text-[10px] text-slate-400 text-right">{body.length}/240</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("Annulla", "Cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white"
            data-testid="portal-push-send"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            {t("Invia", "Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const ShareDialog = ({ open, onOpenChange, url, clientName, t }) => {
  const waText = t(
    `Ciao ${clientName || ""}! Ti ho preparato il tuo Recovery Journey personale. Apri il link per vedere esercizi, progressi e prossimi appuntamenti: ${url}`,
    `Hello ${clientName || ""}! I've prepared your personal Recovery Journey. Open the link to see exercises, progress and upcoming appointments: ${url}`
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("Link copiato", "Link copied"));
    } catch {
      toast.error(t("Errore copia", "Copy error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="portal-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            {t("Patient Portal", "Patient Portal")}
          </DialogTitle>
          <DialogDescription>
            {t("Condividi questo link con il paziente. Da mobile è un'esperienza healthtech premium.",
               "Share this link with the patient. On mobile it's a premium healthtech experience.")}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-2.5 text-xs font-mono break-all text-slate-700 dark:text-slate-200">
          {url}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={copy}
            variant="outline"
            className="rounded-2xl py-5"
            data-testid="portal-share-copy"
          >
            <Copy className="w-4 h-4 mr-2" /> {t("Copia link", "Copy link")}
          </Button>
          <Button
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(waText)}`, "_blank")}
            className="rounded-2xl py-5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
            data-testid="portal-share-whatsapp"
          >
            <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
          </Button>
        </div>
        <Button
          onClick={() => window.open(url, "_blank")}
          variant="ghost"
          className="rounded-full"
          data-testid="portal-share-open"
        >
          <ExternalLink className="w-4 h-4 mr-1.5" /> {t("Apri anteprima", "Open preview")}
        </Button>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("Chiudi", "Close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PatientPortalCard;
